import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import EventCard from '../components/EventCard';
import { Heart, User, EnvelopeSimple, BellRinging } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Seo from '../components/Seo';

export default function Dashboard() {
  const { user, login, loading } = useAuth();
  const [saved, setSaved] = useState([]);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [savingPref, setSavingPref] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/me/saved-events').then(r => setSaved(r.data));
      api.get('/me/email-prefs').then(r => setEmailOptIn(r.data.email_opt_in)).catch(() => {});
    }
  }, [user]);

  const togglePref = async () => {
    setSavingPref(true);
    try {
      const next = !emailOptIn;
      await api.put('/me/email-prefs', { email_opt_in: next });
      setEmailOptIn(next);
      toast.success(next ? 'Email updates ON' : 'Email updates OFF');
    } catch {
      toast.error('Could not update preference');
    } finally { setSavingPref(false); }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await api.post('/me/test-email');
      if (r.data?.ok) toast.success('Test email sent — check your inbox');
      else if (r.data?.disabled) toast.warning('Email not configured yet (admin needs to add RESEND_API_KEY)');
      else toast.error(`Failed: ${r.data?.error || 'unknown'}`);
    } catch (e) {
      toast.error('Failed to send test email');
    } finally { setSendingTest(false); }
  };

  if (loading) return (
    <>
      <Seo path="/dashboard" title="My Dashboard" noindex />
      <div className="max-w-7xl mx-auto p-12"><div className="h-32 bg-muted rounded-2xl animate-pulse" /></div>
    </>
  );
  if (!user) return (
    <>
      <Seo path="/dashboard" title="My Dashboard" noindex />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-4xl mb-4">Sign in to access your dashboard</h1>
        <Button onClick={login} className="bg-forest hover:bg-forest/90 text-bone rounded-full" data-testid="dashboard-signin">Sign in</Button>
      </div>
    </>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo path="/dashboard" title="My Dashboard" noindex />
      <div className="flex items-center gap-4 mb-10">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-muted grid place-items-center"><User size={28} /></div>
        )}
        <div>
          <p className="label text-terracotta">Dashboard</p>
          <h1 className="font-display text-3xl">{user.name}</h1>
        </div>
      </div>

      <section className="mb-10 bg-card border border-border rounded-2xl p-6" data-testid="email-prefs-card">
        <h2 className="font-display text-xl mb-3 flex items-center gap-2">
          <EnvelopeSimple size={20} weight="duotone" className="text-turquoise" /> Email updates
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Get a weekly digest of upcoming pow wows and instant alerts when your saved events are coming up.
          Currently sent to <span className="text-bone">{user.email}</span>.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={togglePref}
            disabled={savingPref}
            className={`${emailOptIn ? 'bg-turquoise text-navy hover:bg-turquoise/90' : 'bg-muted text-bone hover:bg-muted/80'} rounded-full`}
            data-testid="email-optin-toggle"
          >
            <BellRinging size={16} weight="duotone" className="mr-2" />
            {emailOptIn ? 'Subscribed — turn off' : 'Subscribe to updates'}
          </Button>
          <Button
            variant="outline"
            onClick={sendTest}
            disabled={sendingTest || !emailOptIn}
            className="rounded-full"
            data-testid="email-send-test"
          >
            {sendingTest ? 'Sending…' : 'Send me a test email'}
          </Button>
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Heart size={22} weight="duotone" className="text-terracotta" /> Saved events</h2>
        {saved.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-12 text-center">
            <p className="text-muted-foreground mb-4">You haven't saved any events yet.</p>
            <Link to="/events"><Button className="bg-forest hover:bg-forest/90 text-bone rounded-full">Browse events</Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {saved.map(e => <EventCard key={e.event_id} event={e} />)}
          </div>
        )}
      </section>
    </div>
  );
}
